import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, XCircle, Banknote, Smartphone, CreditCard, Wallet } from 'lucide-react';
import { formatDate, formatCurrency } from '@/lib/utils';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FilterBar } from '@/components/common/FilterBar';
import { TablePagination } from '@/components/ui/pagination';
import { useFilters } from '@/hooks/useFilters';
import { usePagination } from '@/hooks/usePagination';
import { paymentsApi, type WithdrawalRecord } from '../api/payments.api';
import toast from 'react-hot-toast';

const paymentStatusVariant: Record<string, any> = {
  completed: 'success',
  pending: 'warning',
  failed: 'destructive',
  refunded: 'secondary',
};

const paymentStatusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const withdrawalStatusVariant: Record<string, any> = {
  completed: 'success',
  pending: 'warning',
  processing: 'outline',
  rejected: 'destructive',
};

function MethodIcon({ method }: { method: string }) {
  const icons: Record<string, React.ElementType> = {
    cash: Banknote,
    upi: Smartphone,
    card: CreditCard,
    wallet: Wallet,
  };
  const Icon = icons[method?.toLowerCase()] ?? CreditCard;
  return (
    <div className="flex items-center gap-1.5 capitalize">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span>{method}</span>
    </div>
  );
}

export function PaymentsListPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('payments');
  const [withdrawalFilter, setWithdrawalFilter] = useState('all');
  const { page, limit, setPage, setLimit } = usePagination();
  const { filters, setFilter, resetFilters } = useFilters({
    defaultValues: { search: '', status: 'all' },
  });
  const [remarksModal, setRemarksModal] = useState<{
    isOpen: boolean;
    id: string;
    action: 'approve' | 'reject';
    driverName: string;
    amount: string;
  } | null>(null);
  const [remarks, setRemarks] = useState('');

  const { data: paymentsData, isLoading: paymentsLoading, refetch: refetchPayments } = useQuery({
    queryKey: ['admin-payments', filters, page, limit],
    queryFn: () => paymentsApi.getAll({
      page,
      limit,
      search: filters.search || undefined,
      status: filters.status && filters.status !== 'all' ? filters.status : undefined,
    }),
    enabled: activeTab === 'payments',
  });

  const { data: withdrawals = [], isLoading: withdrawalsLoading } = useQuery({
    queryKey: ['admin-withdrawals', withdrawalFilter],
    queryFn: () => paymentsApi.getWithdrawals(withdrawalFilter),
    enabled: activeTab === 'withdrawals',
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, remarks }: { id: string; remarks: string }) =>
      paymentsApi.approveWithdrawal(id, remarks),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      toast.success('Withdrawal approved successfully');
      setRemarksModal(null);
      setRemarks('');
    },
    onError: () => toast.error('Failed to approve withdrawal'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, remarks }: { id: string; remarks: string }) =>
      paymentsApi.rejectWithdrawal(id, remarks),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-withdrawals'] });
      toast.success('Withdrawal rejected — wallet refunded');
      setRemarksModal(null);
      setRemarks('');
    },
    onError: () => toast.error('Failed to reject withdrawal'),
  });

  const handleAction = () => {
    if (!remarksModal) return;
    if (remarksModal.action === 'approve') {
      approveMutation.mutate({ id: remarksModal.id, remarks });
    } else {
      rejectMutation.mutate({ id: remarksModal.id, remarks });
    }
  };

  const payments = paymentsData?.payments ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        description="Manage all transactions and driver withdrawal requests"
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="payments">All Payments</TabsTrigger>
          <TabsTrigger value="withdrawals">Driver Withdrawals</TabsTrigger>
        </TabsList>

        {/* ── Payments Tab ── */}
        <TabsContent value="payments" className="mt-4 space-y-4">
          <FilterBar
            filters={[
              {
                type: 'search',
                key: 'search',
                placeholder: 'Search by customer, driver, or transaction ID...',
                className: 'w-80',
              },
              {
                type: 'select',
                key: 'status',
                placeholder: 'Status',
                options: paymentStatusOptions,
              },
            ]}
            values={filters}
            onChange={(key, value) => setFilter(key as keyof typeof filters, value as string)}
            onReset={resetFilters}
            onRefresh={() => refetchPayments()}
          />

          <Card>
            <CardContent className="p-0">
              {paymentsLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Driver</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Driver Earnings</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No payments found
                        </TableCell>
                      </TableRow>
                    ) : (
                      payments.map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {p.transaction_id || p.razorpay_payment_id || p.razorpay_order_id || '—'}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{p.booking?.customer?.name || '—'}</p>
                              {p.booking?.customer?.phone && (
                                <p className="text-xs text-muted-foreground">{p.booking.customer.phone}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{p.booking?.driver?.name || '—'}</p>
                              {p.booking?.driver?.phone && (
                                <p className="text-xs text-muted-foreground">{p.booking.driver.phone}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <MethodIcon method={p.payment_method} />
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(parseFloat(p.amount || '0'))}
                          </TableCell>
                          <TableCell className="text-right">
                            {p.driver_earnings
                              ? <span className="text-success font-medium">{formatCurrency(parseFloat(p.driver_earnings))}</span>
                              : <span className="text-muted-foreground">—</span>
                            }
                          </TableCell>
                          <TableCell>
                            <Badge variant={paymentStatusVariant[p.payment_status] ?? 'secondary'} className="capitalize">
                              {p.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {p.created_at ? formatDate(p.created_at) : '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {paymentsData && paymentsData.pages > 1 && (
            <TablePagination
              currentPage={paymentsData.page}
              totalPages={paymentsData.pages}
              totalItems={paymentsData.total}
              pageSize={limit}
              onPageChange={setPage}
              onPageSizeChange={setLimit}
            />
          )}
        </TabsContent>

        {/* ── Withdrawals Tab ── */}
        <TabsContent value="withdrawals" className="mt-4 space-y-4">
          <div className="flex gap-2">
            {['all', 'pending', 'processing', 'completed', 'rejected'].map((s) => (
              <Button
                key={s}
                variant={withdrawalFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setWithdrawalFilter(s)}
                className="capitalize"
              >
                {s}
              </Button>
            ))}
          </div>

          <Card>
            <CardContent className="p-0">
              {withdrawalsLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Driver</TableHead>
                      <TableHead>Bank Details</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(withdrawals as WithdrawalRecord[]).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No withdrawal requests found
                        </TableCell>
                      </TableRow>
                    ) : (
                      (withdrawals as WithdrawalRecord[]).map((w) => {
                        const driverName = w.driver_profile?.user?.name || '—';
                        const driverPhone = w.driver_profile?.user?.phone;
                        const bank = w.bank_account;

                        return (
                          <TableRow key={w.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{driverName}</p>
                                {driverPhone && (
                                  <p className="text-xs text-muted-foreground">{driverPhone}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {bank ? (
                                <div className="text-sm">
                                  <p className="font-medium">{bank.bank_name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {bank.account_number} · {bank.ifsc_code}
                                  </p>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(parseFloat(w.amount || '0'))}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={withdrawalStatusVariant[w.status] ?? 'secondary'}
                                className="capitalize"
                              >
                                {w.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground max-w-[140px] truncate">
                              {w.remarks || '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {formatDate(w.created_at)}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {w.status !== 'pending' ? formatDate(w.updated_at) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              {w.status === 'pending' && (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Approve"
                                    onClick={() =>
                                      setRemarksModal({
                                        isOpen: true,
                                        id: w.id,
                                        action: 'approve',
                                        driverName,
                                        amount: w.amount,
                                      })
                                    }
                                  >
                                    <CheckCircle className="h-4 w-4 text-success" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title="Reject"
                                    onClick={() =>
                                      setRemarksModal({
                                        isOpen: true,
                                        id: w.id,
                                        action: 'reject',
                                        driverName,
                                        amount: w.amount,
                                      })
                                    }
                                  >
                                    <XCircle className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Remarks Modal ── */}
      {remarksModal && (
        <Modal
          isOpen={remarksModal.isOpen}
          onClose={() => { setRemarksModal(null); setRemarks(''); }}
        >
          <ModalHeader onClose={() => { setRemarksModal(null); setRemarks(''); }}>
            <ModalTitle>
              {remarksModal.action === 'approve' ? 'Approve Withdrawal' : 'Reject Withdrawal'}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              <div className="rounded-lg bg-muted p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Driver:</span> <strong>{remarksModal.driverName}</strong></p>
                <p><span className="text-muted-foreground">Amount:</span> <strong>{formatCurrency(parseFloat(remarksModal.amount))}</strong></p>
              </div>
              {remarksModal.action === 'reject' && (
                <p className="text-xs text-muted-foreground">
                  Rejecting will refund the amount back to the driver's wallet.
                </p>
              )}
              <div className="space-y-1.5">
                <Label>Remarks (optional)</Label>
                <Input
                  placeholder={
                    remarksModal.action === 'approve'
                      ? 'e.g. Payment processed via NEFT'
                      : 'e.g. Insufficient bank details'
                  }
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={() => { setRemarksModal(null); setRemarks(''); }}>
              Cancel
            </Button>
            <Button
              variant={remarksModal.action === 'approve' ? 'default' : 'destructive'}
              onClick={handleAction}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              {approveMutation.isPending || rejectMutation.isPending
                ? 'Processing...'
                : remarksModal.action === 'approve'
                  ? 'Approve'
                  : 'Reject & Refund'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}
